import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import API from '../api';
import { useUser } from '../context/UserContext';
import NewCustomerModal from './NewCustomerModal';
import EditCustomerModal from './EditCustomerModal';
import ChangePasswordModal from './ChangePasswordModal';

export default function Dashboard() {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [customersPerPage, setCustomersPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());
  const [columns, setColumns] = useState({
    id: true,
    created_at: false,
    customer_number: true,
    company_name: true,
    address_for_field_engineer: true,
    onsite_contact_name: true,
    onsite_contact_phone: true,
    onsite_contact_email: true,
    regular_business_hours: true,
    emergency_business_hours: true,
    notes: true,
  });
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, canEdit, isAdmin, logout: contextLogout } = useUser();
  const formatTimestamp = (timestamp) => {
      const date = new Date(timestamp);

      // Option A: "2025-05-29 11:40 UTC"
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // months are zero-indexed
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

      return `${year}-${month}-${day} ${hours}:${minutes} ${tz}`;
  };

  // Check auth on load
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert("You must be logged in.");
      navigate('/');
    }
  }, [navigate]);

  const fetchCustomers = async () => {
    try {
      const res = await API.get('/customers');
      setCustomers(res.data);
      setFilteredCustomers(res.data);
    } catch (err) {
      console.error("Error fetching customers:", err);
      if (err.response?.status === 401) {
        alert('Session expired. Please log in again.');
        navigate('/');
      } else {
        alert('Error loading customers');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await API.delete(`/customers/${id}`);
      fetchCustomers();
      setSuccessMessage('Customer has been successfully deleted.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch {
      alert('Failed to delete customer');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCustomers.size === 0) {
      alert('Please select at least one customer to delete.');
      return;
    }

    const count = selectedCustomers.size;
    if (!window.confirm(`Are you sure you want to delete ${count} customer${count > 1 ? 's' : ''}?`)) return;

    try {
      const customerIds = Array.from(selectedCustomers);
      await API.post('/customers/bulk-delete', { customerIds });
      setSelectedCustomers(new Set());
      fetchCustomers();
      setSuccessMessage(`${count} customer${count > 1 ? 's have' : ' has'} been successfully deleted.`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Bulk delete error:', err);
      alert('Failed to delete selected customers');
    }
  };

  const toggleSelectCustomer = (customerId) => {
    setSelectedCustomers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(customerId)) {
        newSet.delete(customerId);
      } else {
        newSet.add(customerId);
      }
      return newSet;
    });
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setShowEditModal(true);
  };

  const handleLogout = async () => {
    try {
      await API.post('/logout');
      contextLogout(); // Clear user context
      delete API.defaults.headers.common['Authorization'];
      navigate('/');
    } catch (err) {
      alert('Failed to logout');
    }
  };

 const exportToCSV = () => {
  // Ensure created_at is first, then other columns in their original order
  const header = [
    'created_at', 
    ...Object.keys(columns)
      .filter(col => columns[col] && col !== 'created_at')
  ];
  
  const rows = filteredCustomers.map(p => 
    header.map(h => {
      // Format the timestamp if this is the created_at column
      if (h === 'created_at' && p[h]) {
        return `"${formatTimestamp(p[h])}"`;
      }
      return `"${p[h] ?? ''}"`;
    }).join(',')
  );
  
  const csv = [header.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'customers.csv';
  a.click();
  URL.revokeObjectURL(url);
};

  const toggleColumn = (col) => {
    setColumns(prev => ({ ...prev, [col]: !prev[col] }));
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setCurrentPage(1);
    if (query === '') {
      setFilteredCustomers(customers);
    } else {
      const filtered = customers.filter(customer =>
        Object.values(customer).some(value =>
          value && value.toString().toLowerCase().includes(query)
        )
      );
      setFilteredCustomers(filtered);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);

    const sorted = [...filteredCustomers].sort((a, b) => {
      const valA = a[key] ?? '';
      const valB = b[key] ?? '';

      // Handle different data types
      if (key === 'created_at') {
        const dateA = new Date(valA).getTime() || 0;
        const dateB = new Date(valB).getTime() || 0;
        return direction === 'asc' ? dateA - dateB : dateB - dateA;
      }

      // Handle numbers
      if (['id', 'customer_number', 'onsite_contact_phone'].includes(key)) {
        const numA = Number(valA) || 0;
        const numB = Number(valB) || 0;
        return direction === 'asc' ? numA - numB : numB - numA;
      }

      // Default to string comparison
      const strA = valA.toString().toLowerCase();
      const strB = valB.toString().toLowerCase();
      return direction === 'asc'
        ? strA.localeCompare(strB)
        : strB.localeCompare(strA);
    });

    setFilteredCustomers(sorted);
  };

  const getSortIndicator = (key) => {
  const isActive = sortConfig.key === key;

  return (
    <span className="inline-flex items-center ml-1 text-gray-400">
      <span className={isActive && sortConfig.direction === 'asc' ? 'text-blue-600' : ''}>▲</span>
      <span className="mx-1">/</span>
      <span className={isActive && sortConfig.direction === 'desc' ? 'text-blue-600' : ''}>▼</span>
    </span>
   );
  };

  const indexOfLast = currentPage * customersPerPage;
  const indexOfFirst = indexOfLast - customersPerPage;
  const visibleCustomers = filteredCustomers.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredCustomers.length / customersPerPage);

  const toggleSelectAll = () => {
    if (selectedCustomers.size === visibleCustomers.length) {
      setSelectedCustomers(new Set());
    } else {
      setSelectedCustomers(new Set(visibleCustomers.map(c => c.id)));
    }
  };

  const isAllSelected = visibleCustomers.length > 0 && selectedCustomers.size === visibleCustomers.length;

  // Generate page numbers to display (max 5 pages shown at a time)
  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    const pages = [];

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    return pages;
  };

  const handleItemsPerPageChange = (e) => {
    setCustomersPerPage(parseInt(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPreviousPage = () => setCurrentPage(Math.max(1, currentPage - 1));
  const goToNextPage = () => setCurrentPage(Math.min(totalPages, currentPage + 1));

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (location.search.includes('refresh=true')) {
      fetchCustomers();
      navigate('/dashboard', { replace: true });
    }
  }, [location, navigate]);

 return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 to-blue-200 p-6">
      <div className="w-[80%] mx-auto bg-white p-8 rounded-xl shadow-xl">
        {/* Success message */}
        {successMessage && (
          <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded shadow text-center font-semibold">
            {successMessage}
          </div>
        )}
        <div className="mb-6">
          {/* Header with Logo and Profile */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              {/* Logo Image */}
              <img
                src={`${process.env.PUBLIC_URL}/images/dispatch-truck.svg`}
                alt="Tech Dispatch Logo"
                className="h-16 w-auto object-contain"
              />
              <div>
                <h2 className="text-3xl font-bold text-blue-700">Tech Dispatch</h2>
              </div>
            </div>

            {/* Profile Section */}
            {user && (
              <div className="flex items-center gap-3">
                <div className="text-sm text-gray-600">
                  <span>
                    Logged as: <span className="font-semibold">{user.name}, {user.last_name}</span>
                    <span className="ml-3">
                      Role: <span className={`ml-1 px-2 py-1 text-xs rounded-full ${
                        user.role === 'admin' ? 'bg-red-100 text-red-800' :
                        user.role === 'editor' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {user.role?.toUpperCase()}
                      </span>
                    </span>
                  </span>
                </div>
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded-lg shadow-md transition"
                  title="Change your password"
                >
                  Change Password
                </button>
                <button
                  onClick={handleLogout}
                  className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg shadow-md transition"
                >
                  Logout
                </button>
              </div>
            )}
          </div>

          {/* Tools Section */}
          <div className="flex justify-start items-center gap-2 pl-5">
            <span className="text-sm font-semibold text-gray-700 mr-2">Tools:</span>
            <button
              onClick={exportToCSV}
              className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg shadow-md transition text-sm"
            >
              Export CSV
            </button>
            {canEdit() && (
              <>
                <Link
                  to="/bulk-upload?redirect=/dashboard"
                  className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-4 py-2 rounded-lg shadow-md transition text-sm"
                >
                  Bulk Upload Customers
                </Link>
                <button
                  onClick={() => setShowNewModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg shadow-md transition text-sm"
                >
                  Add Customer
                </button>
              </>
            )}
            <Link
              to="/history"
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded-lg shadow-md transition text-sm"
            >
              Audit
            </Link>
            {isAdmin() && (
              <>
                <Link
                  to="/users"
                  className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold px-4 py-2 rounded-lg shadow-md transition text-sm"
                >
                  Manage Users
                </Link>
                <Link
                  to="/api-tokens"
                  className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-4 py-2 rounded-lg shadow-md transition text-sm"
                >
                  API Token Management
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Search customers..."
            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="mb-4">
          <strong className="text-gray-700">Toggle Columns:</strong>
          <div className="flex flex-wrap gap-2 mt-2">
            {Object.keys(columns).map(col => (
              <label key={col} className="inline-flex items-center">
                <input
                  type="checkbox"
                  checked={columns[col]}
                  onChange={() => toggleColumn(col)}
                  className="mr-1"
                />
                <span className="text-sm text-gray-700">{col.replace(/_/g, ' ')}</span>
              </label>
            ))}
          </div>
        </div>
        {filteredCustomers.length === 0 ? (
          <p className="text-center text-gray-700 text-lg">
            {searchQuery ? 'No customers match your search.' : 'Welcome! You have no customers yet.'}
          </p>
        ) : (
          <>
            {/* Bulk Actions Section */}
            {canEdit() && selectedCustomers.size > 0 && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-gray-700">
                      {selectedCustomers.size} customer{selectedCustomers.size > 1 ? 's' : ''} selected
                    </span>
                    <button
                      onClick={() => setSelectedCustomers(new Set())}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      Clear selection
                    </button>
                  </div>
                  <button
                    onClick={handleBulkDelete}
                    className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-2 rounded-lg shadow-md transition flex items-center gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Delete Selected ({selectedCustomers.size})
                  </button>
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto border-collapse border border-gray-300 text-sm whitespace-nowrap">
                <thead className="bg-blue-100 text-gray-700">
                  <tr>
                    {canEdit() && (
                      <th className="border border-gray-300 px-4 py-2">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 cursor-pointer"
                          title="Select all on this page"
                        />
                      </th>
                    )}
                    <th className="border border-gray-300 px-4 py-2">Actions</th>
                    {columns.id && (
                      <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('id')}>
                        ID{getSortIndicator('id')}
                      </th>
                    )}
                    {columns.created_at && (
                      <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('created_at')}>
                        Created At{getSortIndicator('created_at')}
                      </th>
                    )}
                    {columns.customer_number && (
                      <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('customer_number')}>
                        Customer Number{getSortIndicator('customer_number')}
                      </th>
                    )}
                    {columns.company_name && (
                      <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('company_name')}>
                        Company Name{getSortIndicator('company_name')}
                      </th>
                    )}
                    {columns.address_for_field_engineer && (
                      <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('address_for_field_engineer')}>
                        Address for Field Engineer{getSortIndicator('address_for_field_engineer')}
                      </th>
                    )}
                    {columns.onsite_contact_name && (
                      <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('onsite_contact_name')}>
                        Onsite Contact Name{getSortIndicator('onsite_contact_name')}
                      </th>
                    )}
                    {columns.onsite_contact_phone && (
                      <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('onsite_contact_phone')}>
                        Onsite Contact Phone{getSortIndicator('onsite_contact_phone')}
                      </th>
                    )}
                    {columns.onsite_contact_email && (
                      <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('onsite_contact_email')}>
                        Onsite Contact Email{getSortIndicator('onsite_contact_email')}
                      </th>
                    )}
                    {columns.regular_business_hours && (
                      <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('regular_business_hours')}>
                        Regular Business Hours{getSortIndicator('regular_business_hours')}
                      </th>
                    )}
                    {columns.emergency_business_hours && (
                      <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('emergency_business_hours')}>
                        Emergency Business Hours{getSortIndicator('emergency_business_hours')}
                      </th>
                    )}
                    {columns.notes && (
                      <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('notes')}>
                        Notes{getSortIndicator('notes')}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {visibleCustomers.map(p => (
                    <tr key={p.id} className="hover:bg-gray-100">
                        {canEdit() && (
                          <td className="border border-gray-200 px-4 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={selectedCustomers.has(p.id)}
                              onChange={() => toggleSelectCustomer(p.id)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        )}
                        <td className="border border-gray-200 px-4 py-2 text-center space-x-2">
                          {canEdit() && (
                            <>
                              <button
                                onClick={() => handleEdit(p)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-2 py-1 rounded"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(p.id)}
                                className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded"
                              >
                                Delete
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => navigate(`/history/${p.id}`)}
                            className="bg-indigo-500 hover:bg-indigo-600 text-white px-2 py-1 rounded"
                          >
                            View History
                          </button>
                        </td>
                      {columns.id && <td className="border border-gray-200 px-4 py-2">{p.id}</td>}
                      {columns.created_at && (
                        <td className="border border-gray-200 px-4 py-2">
                          {formatTimestamp(p.created_at)}
                        </td>
                      )}
                      {columns.customer_number && <td className="border border-gray-200 px-4 py-2">{p.customer_number}</td>}
                      {columns.company_name && <td className="border border-gray-200 px-4 py-2">{p.company_name}</td>}
                      {columns.address_for_field_engineer && <td className="border border-gray-200 px-4 py-2">{p.address_for_field_engineer}</td>}
                      {columns.onsite_contact_name && <td className="border border-gray-200 px-4 py-2">{p.onsite_contact_name}</td>}
                      {columns.onsite_contact_phone && <td className="border border-gray-200 px-4 py-2">{p.onsite_contact_phone}</td>}
                      {columns.onsite_contact_email && <td className="border border-gray-200 px-4 py-2">{p.onsite_contact_email}</td>}
                      {columns.regular_business_hours && <td className="border border-gray-200 px-4 py-2">{p.regular_business_hours}</td>}
                      {columns.emergency_business_hours && <td className="border border-gray-200 px-4 py-2">{p.emergency_business_hours}</td>}
                      {columns.notes && <td className="border border-gray-200 px-4 py-2 max-w-xs truncate" title={p.notes}>{p.notes}</td>}

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="mt-6 space-y-3">
              {/* Pagination Info */}
              <div className="text-center text-sm text-gray-600">
                Showing page {currentPage} of {totalPages} ({filteredCustomers.length} total {filteredCustomers.length === 1 ? 'customer' : 'customers'})
              </div>

              {/* Pagination Controls */}
              <div className="flex justify-center items-center space-x-2">
                {/* First Button */}
                <button
                  onClick={goToFirstPage}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded border ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  First
                </button>

                {/* Previous Button */}
                <button
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded border ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 rounded border ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}

                {/* Next Button */}
                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded border ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  Next
                </button>

                {/* Last Button */}
                <button
                  onClick={goToLastPage}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded border ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  Last
                </button>
              </div>

              {/* Items Per Page Selector */}
              <div className="flex justify-center items-center space-x-2 text-sm text-gray-600">
                <label htmlFor="itemsPerPage">Items per page:</label>
                <select
                  id="itemsPerPage"
                  value={customersPerPage}
                  onChange={handleItemsPerPageChange}
                  className="px-2 py-1 border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>
          </>
        )}
        {/* Modals */}
        {showNewModal && (
          <NewCustomerModal
            onClose={() => setShowNewModal(false)}
            onCustomerCreated={fetchCustomers}
          />
        )}
        {showEditModal && editingCustomer && (
          <EditCustomerModal
            customer={editingCustomer}
            onClose={() => setShowEditModal(false)}
            onCustomerUpdated={fetchCustomers}
          />
        )}
        <ChangePasswordModal
          isOpen={showPasswordModal}
          onClose={() => setShowPasswordModal(false)}
          userId={user?.id}
          userName={`${user?.name} ${user?.last_name}`}
          isOwnPassword={true}
        />
      </div>
    </div>
  );
}